// RentMapView.jsx
import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { getMapProperties } from './api.js';
import CopyFailureAlert from '../Alertpage/CopyAlert/CopyFailureAlert';
import CopySuccessAlert from '../Alertpage/CopyAlert/CopySuccessAlert';
import { GOOGLEMAPS_API_KEY } from '../../Api/ApiUrls.js';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const PRICE_TAG_ZOOM = 13;
const CLUSTER_LABEL_MAX_ZOOM = 8;

// ─── module‑level cache ──────────────────────────────────────────────
let cachedProperties = null;
let dataLoaded = false;

// 🔍 DEBUG: Make cache accessible globally
if (typeof window !== 'undefined') {
  window.__mapCache = {
    get properties() { return cachedProperties; },
    get loaded() { return dataLoaded; },
    clear: () => { cachedProperties = null; dataLoaded = false; }
  };
}

const _stateCache = new Map();
const _pendingRequests = new Map();

const GOOGLE_API_KEY = GOOGLEMAPS_API_KEY;

const getStateName = async (lat, lng) => {
  const key = `${parseFloat(lat).toFixed(2)},${parseFloat(lng).toFixed(2)}`;

  if (_stateCache.has(key)) return _stateCache.get(key);
  if (_pendingRequests.has(key)) return _pendingRequests.get(key);

  const promise = (async () => {
    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=administrative_area_level_1&key=${GOOGLE_API_KEY}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Google Geocoding error: ${res.status}`);

      const data = await res.json();

      if (data.status !== 'OK' || !data.results?.length) {
        throw new Error(`No results: ${data.status}`);
      }

      const components = data.results[0].address_components;
      const stateComponent = components.find(c =>
        c.types.includes('administrative_area_level_1')
      );

      const name = stateComponent?.long_name || 'India';
      _stateCache.set(key, name);
      return name;

    } catch (err) {
      console.warn('Google reverse geocode failed:', err);
      const fallback = 'India';
      _stateCache.set(key, fallback);
      return fallback;
    } finally {
      _pendingRequests.delete(key);
    }
  })();

  _pendingRequests.set(key, promise);
  return promise;
};

// ── cluster state label — white card to the RIGHT of the cluster bubble ──────
const buildClusterLabelIcon = (text, loading = false) => L.divIcon({
  className: 'csl-wrap',
  html: `
    <div style="
      display:flex;align-items:center;
      pointer-events:none;
      transform:translateY(-50%);
    ">
      <div style="width:14px;height:2px;background:#1565C0;flex-shrink:0;"></div>
      <div style="
        background:#fff;
        border-radius:10px;
        padding:4px 10px;
        box-shadow:0 3px 10px rgba(0,0,0,0.22);
        border-left:3px solid #1565C0;
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;
        font-size:11px;font-weight:700;color:#1a1a1a;
        white-space:nowrap;
        ${loading ? 'opacity:.55;' : ''}
      ">
        ${loading ? '⏳ …' : `${text}`}
      </div>
    </div>`,
  iconSize: [180, 22],
  iconAnchor: [0, 11],
});

// ✅ FIXED: Matches buy module price tag UI (selected/unselected states)
const buildSimplePriceTagIcon = (price, categoryName, isSelected = false, categoryColor = '#1a73e8') => {
const formatPriceForTag = (price) => {
  const num = Number(price);

  // Round to 2 decimals, then strip trailing ".00" / trailing "0"
  // 10       -> "10"
  // 10.5     -> "10.5"
  // 10.25    -> "10.25"
  // 10.50    -> "10.5"
  const trim = (n) => {
    const fixed = n.toFixed(2);
    return fixed.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
  };

  if (num >= 10000000) return `₹${trim(num / 10000000)}Cr`;
  if (num >= 100000)   return `₹${trim(num / 100000)}L`;
  if (num >= 1000)     return `₹${trim(num / 1000)}K`;
  return `₹${num.toLocaleString('en-IN')}`;
};

  const formattedPrice = formatPriceForTag(price);

  const BROWN      = '#8B4716';   // circle background
  const ICON_COLOR = '#ffffff';   // white icon

  // ✅ Dynamic colors based on selection (same as buy module)
  const PILL_BG    = isSelected ? '#8B4716' : '#ffffff';
  const TEXT_COLOR = isSelected ? '#ffffff' : '#1a1a1a';

  // ✅ Black border and expanding shadow on selection
  const BORDER = isSelected ? '2px solid #000000' : '1px solid rgba(0,0,0,0.06)';

  const SHADOW = isSelected
    ? '0 0 0 6px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0,0,0,0.4)'
    : '0 2px 6px rgba(0,0,0,0.22)';

  const SCALE = isSelected ? 'scale(1.12)' : 'scale(1)';

  const ICON_SIZE = 20;

  const svg = (path) =>
    `<svg viewBox="0 0 24 24" width="${ICON_SIZE}" height="${ICON_SIZE}" fill="${ICON_COLOR}"><path d="${path}"/></svg>`;

  const getCategorySVG = (name) => {
    const n = (name || '').toLowerCase().trim();

    if (n.includes('apartment')) {
      return svg('M17 11V3H7v4H3v14h8v-4h2v4h8V11h-4zM7 19H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm4 4H9v-2h2v2zm0-4H9V9h2v2zm0-4H9V5h2v2zm4 8h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm4 12h-2v-2h2v2zm0-4h-2v-2h2v2z');
    }
    if (n.includes('commercial building') || n === 'commercial') {
      return svg('M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z');
    }
    if (n.includes('commercial land') || n.includes('plot') || n.includes('land')) {
      return svg('M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z');
    }
    if (n.includes('villa') || n.includes('duplex')) {
      return svg('M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z');
    }
    if (n.includes('flat')) {
      return svg('M19 9.3V4h-3v2.6L12 3 2 12h3v8h5v-6h4v6h5v-8h3l-3-2.7zm-9 .7c0-1.1.9-2 2-2s2 .9 2 2h-4z');
    }
    if (n.includes('house') || n.includes('rental')) {
      return svg('M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z');
    }
    if (n.includes('pg') || n.includes('office')) {
      return svg('M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z');
    }
    if (n.includes('sell')) {
      return svg('M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z');
    }
    return svg('M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z');
  };

  const innerSVG = getCategorySVG(categoryName);

  return L.divIcon({
    className: 'custom-price-tag',
    html: `
      <div style="
        position: relative;
        display: inline-flex;
        align-items: center;
        height: 38px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
        white-space: nowrap;
        line-height: 1;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        transform: ${SCALE};
        transform-origin: bottom center;
      ">
        <!-- White ring + brown circle + white icon -->
        <div style="
          position: relative;
          z-index: 2;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          border: ${BORDER};
          box-shadow: ${SHADOW};
          flex-shrink: 0;
          transition: box-shadow 0.3s ease, border 0.3s ease;
        ">
          <div style="
            width: 28px;
            height: 28px;
            border-radius: 50%;
            background: ${BROWN};
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            ${innerSVG}
          </div>
        </div>

        <!-- Price pill, tucked behind the circle -->
        <div style="
          position: relative;
          z-index: 1;
          margin-left: -20px;
          height: 28px;
          padding: 0 10px 0 24px;
          background: ${PILL_BG};
          border-radius: 999px;
          display: flex;
          align-items: center;
          box-sizing: border-box;
          border: ${BORDER};
          box-shadow: ${SHADOW};
          transition: background 0.3s ease, box-shadow 0.3s ease, border 0.3s ease;
        ">
          <span style="
            font-size: 11px;
            font-weight: 700;
            color: ${TEXT_COLOR};
            line-height: 1;
            transition: color 0.3s ease;
          ">${formattedPrice}</span>
        </div>

        <!-- Bottom tail under the circle -->
        <div style="
          position: absolute;
          z-index: 3;
          bottom: -5px;
          left: 15px;
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 5px solid ${PILL_BG};
          transition: border-top 0.3s ease;
        "></div>
      </div>
    `,
    iconSize: [null, 38],
    iconAnchor: [19, 43],
    popupAnchor: [0, -43],
  });
};

const getMarkerIcon = (categoryName, isSelected = false, categoryColorMap = {}) => {
  const color = categoryColorMap[categoryName] || '#E53935';

  if (isSelected) {
    return L.divIcon({
      html: `<div style="
        background-color: ${color};
        width: 28px; 
        height: 28px;
        border-radius: 50%;
        border: 4px solid #FFD600;
        display: flex; 
        align-items: center; 
        justify-content: center;
        box-shadow: 0 0 20px rgba(255,214,0,0.6);
      ">
        <div style="
          width: 10px;
          height: 10px;
          background-color: white;
          border-radius: 50%;
        "></div>
      </div>`,
      className: 'custom-marker-selected',
      iconSize: [28, 28],
      popupAnchor: [0, -14]
    });
  }

  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: 18px; 
      height: 18px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    className: 'custom-marker',
    iconSize: [18, 18],
    popupAnchor: [0, -9]
  });
};

const RentMapView = ({
  onMapReady,
  onViewportChange,
  onPropertiesUpdate,
  filters,
  selectedId,
  onMarkerClick,
  flyTarget,
  categoryColorMap = {},
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterGroupRef = useRef(null);
  const markerByIdRef = useRef(new Map());
  const debounceRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const [properties, setProperties] = useState(dataLoaded ? cachedProperties : []);
  const [mapReady, setMapReady] = useState(false);
  const [isMapInitializing, setIsMapInitializing] = useState(true);
  const [currentZoom, setCurrentZoom] = useState(5);
  const isFlyingRef = useRef(false);
  const [showInitialSpinner, setShowInitialSpinner] = useState(!dataLoaded);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showNoPropsMsg, setShowNoPropsMsg] = useState(false);
  const [noPropsReason, setNoPropsReason] = useState(null);
  const filtersRef = useRef(filters);
  const clusterLabelLayerRef = useRef(null);
  const clusterLabelMapRef = useRef(new Map());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const showAlert = (type, message) => {
    if (type === 'success') {
      setAlertMessage(message); setShowSuccess(true);
      setTimeout(() => { setShowSuccess(false); setAlertMessage(''); }, 3000);
    } else if (type === 'failure') {
      setAlertMessage(message); setShowFailure(true);
      setTimeout(() => { setShowFailure(false); setAlertMessage(''); }, 3000);
    }
  };

  const fetchProperties = useCallback(async (viewport) => {
    if (!viewport || !mapRef.current) return;
    try {
      console.log('🔄 FETCHING properties from API...');
      const data = await getMapProperties({
        bounds: viewport.bounds, zoom: viewport.zoom,
        propertyType: filters.propertyType,
        priceMin: filters.priceMin, priceMax: filters.priceMax,
      });
      if (data.success && data.properties) {
        console.log(`✅ Fetched ${data.properties.length} properties`);
        setProperties(data.properties);
        onPropertiesUpdate?.(data.properties, showAlert);
        setShowNoPropsMsg(data.properties.length === 0);

        cachedProperties = data.properties;
        dataLoaded = true;

        console.log('💾 CACHE UPDATED:', {
          count: cachedProperties.length,
          sample: cachedProperties.slice(0, 2).map(p => ({
            id: p.id,
            title: p.title,
            price: p.price,
            category: p.categoryName
          }))
        });
      } else {
        console.warn('⚠️ No properties returned from API');
        onPropertiesUpdate?.([]);
        setShowNoPropsMsg(true);
        cachedProperties = [];
        dataLoaded = true;
      }
    } catch (error) {
      console.error('❌ Error fetching properties:', error);
      onPropertiesUpdate?.([]);
      setShowNoPropsMsg(true);
    }
  }, [filters, onPropertiesUpdate]);

  const getCurrentViewport = useCallback(() => {
    if (!mapRef.current) return null;
    try {
      const map = mapRef.current;
      if (!map.getBounds) return null;
      const bounds = map.getBounds();
      if (!bounds) return null;
      return {
        bounds: {
          south: bounds.getSouth(), north: bounds.getNorth(),
          west: bounds.getWest(), east: bounds.getEast()
        },
        zoom: map.getZoom(),
      };
    } catch (error) {
      console.error('Error getting viewport:', error);
      return null;
    }
  }, []);

  const refreshClusterLabels = useCallback(() => {
    const map = mapRef.current;
    const layer = clusterLabelLayerRef.current;
    if (!map || !layer) return;

    const zoom = map.getZoom();

    if (zoom >= CLUSTER_LABEL_MAX_ZOOM) {
      layer.clearLayers();
      clusterLabelMapRef.current.clear();
      return;
    }

    const visibleClusters = [];
    map.eachLayer(l => {
      if (l instanceof L.MarkerCluster) {
        visibleClusters.push(l);
      }
    });

    if (!visibleClusters.length) return;

    layer.clearLayers();
    clusterLabelMapRef.current.clear();

    visibleClusters.forEach(cluster => {
      const { lat, lng } = cluster.getLatLng();
      const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;

      const loadingMarker = L.marker([lat, lng], {
        icon: buildClusterLabelIcon('', true),
        interactive: false,
        zIndexOffset: 9999,
      });
      layer.addLayer(loadingMarker);
      clusterLabelMapRef.current.set(key, loadingMarker);

      getStateName(lat, lng).then(name => {
        const existing = clusterLabelMapRef.current.get(key);
        if (!existing) return;
        existing.setIcon(buildClusterLabelIcon(name, false));
      });
    });
  }, []);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (!mapReady || isMapInitializing) return;

    const MIN_LOADER_DISPLAY_MS = 800;

    const refreshData = async () => {
      if (isRefreshing || isFlyingRef.current) return;
      setIsRefreshing(true);
      setShowInitialSpinner(true);

      try {
        console.log('🔄 Periodic refresh: clearing cache and refetching...');
        cachedProperties = null;
        dataLoaded = false;

        const viewport = getCurrentViewport();
        if (viewport) {
          const minDelay = new Promise(resolve => setTimeout(resolve, MIN_LOADER_DISPLAY_MS));
          await Promise.all([fetchProperties(viewport), minDelay]);
        } else {
          console.warn('⚠️ No viewport for periodic refresh');
          await new Promise(resolve => setTimeout(resolve, MIN_LOADER_DISPLAY_MS));
        }
      } catch (err) {
        console.error('Periodic refresh error:', err);
      } finally {
        setShowInitialSpinner(false);
        setIsRefreshing(false);
      }
    };

    refreshTimerRef.current = setInterval(refreshData, 1800000);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [mapReady, isMapInitializing, getCurrentViewport, fetchProperties]);

  // Initialize map
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    console.log('Initializing map...');
    setIsMapInitializing(true);

    const savedMapState = JSON.parse(localStorage.getItem('rentmapState') || 'null');

    const map = L.map(containerRef.current, {
      center: savedMapState ? [savedMapState.lat, savedMapState.lng] : [20.5937, 78.9629],
      zoom: savedMapState ? savedMapState.zoom : 5,
      minZoom: 4,
      maxZoom: 18,
      zoomControl: true,
    });
    mapRef.current = map;

    const style = document.createElement('style');
    style.textContent = `.leaflet-top { top: 140px !important; }`;
    document.head.appendChild(style);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19, minZoom: 4,
    }).addTo(map);

    clusterLabelLayerRef.current = L.layerGroup().addTo(map);

    const clusterGroup = L.markerClusterGroup({
      chunkedLoading: true,
      disableClusteringAtZoom: 14,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      iconCreateFunction: (cluster) => {
        const firstChild = cluster.getAllChildMarkers()[0];
        const categoryName = firstChild?.options?.categoryName || '';
        const color = categoryColorMap[categoryName] || '#1976D2';

        const zoom = cluster._group._map.getZoom();
        const count = cluster.getChildCount();

        const showCount = zoom >= 10;

        return L.divIcon({
          html: `<div style="
            background-color: ${color};
            border-radius: 6px;
            min-width: 20px;
            height: 20px;
            padding: 0 5px;
            border: 2px solid white;
            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 2px;
            color: white;
            font-size: 10px;
            font-weight: bold;
            font-family: Arial, sans-serif;
            white-space: nowrap;
            box-sizing: border-box;
          ">
            ${showCount ? `<span>${count}</span><span style="font-size:11px;line-height:1;">+</span>` : ''}
          </div>`,
          className: 'custom-cluster-icon',
          iconSize: null,
        });
      }
    });
    clusterGroupRef.current = clusterGroup;
    map.addLayer(clusterGroup);

    const updateZoom = () => {
      if (mapRef.current) setCurrentZoom(mapRef.current.getZoom());
    };
    map.on('zoomend', updateZoom);

    clusterGroup.on('animationend', () => {
      setTimeout(() => refreshClusterLabels(), 120);
    });

    map.on('moveend', () => {
      if (map.getZoom() < CLUSTER_LABEL_MAX_ZOOM) {
        setTimeout(() => refreshClusterLabels(), 120);
      }
    });

    map.whenReady(() => {
      console.log('Map is fully ready');
      setIsMapInitializing(false);
      setMapReady(true);
      onMapReady?.(map);
      updateZoom();

      if (dataLoaded && cachedProperties) {
        console.log('📦 USING CACHED DATA:', {
          count: cachedProperties.length,
          sample: cachedProperties.slice(0, 3).map(p => ({
            id: p.id,
            title: p.title,
            price: p.price
          }))
        });

        setProperties(cachedProperties);
        onPropertiesUpdate?.(cachedProperties, showAlert);
        setShowNoPropsMsg(cachedProperties.length === 0);
        setShowInitialSpinner(false);
      } else {
        console.log('🔄 FIRST LOAD - Fetching from API...');
        const viewport = getCurrentViewport();
        if (viewport) {
          fetchProperties(viewport);
        }
        setTimeout(() => setShowInitialSpinner(false), 5000);
      }
    });

    const handleMoveEnd = () => {
      if (!mapRef.current) return;
      const viewport = getCurrentViewport();
      if (!viewport) return;
      onViewportChange?.(viewport);

      const center = mapRef.current.getCenter();
      localStorage.setItem('rentstate', JSON.stringify({
        zoom: mapRef.current.getZoom(),
        lat: center.lat,
        lng: center.lng,
      }));

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (mapRef.current && !isFlyingRef.current) {
          const currentViewport = getCurrentViewport();
          if (currentViewport) {
            (async () => {
              try {
                const data = await getMapProperties({
                  bounds: currentViewport.bounds,
                  zoom: currentViewport.zoom,
                  propertyType: filtersRef.current.propertyType,
                  priceMin: filtersRef.current.priceMin,
                  priceMax: filtersRef.current.priceMax,
                });

                if (data.success && data.properties) {
                  setProperties(data.properties);
                  onPropertiesUpdate?.(data.properties, showAlert);
                  setShowNoPropsMsg(data.properties.length === 0);
                  cachedProperties = data.properties;
                  dataLoaded = true;

                  console.log('🔄 CACHE UPDATED after zoom/pan:', {
                    count: cachedProperties.length
                  });
                } else {
                  onPropertiesUpdate?.([]);
                  setShowNoPropsMsg(true);
                  cachedProperties = [];
                  dataLoaded = true;
                }
              } catch (error) {
                console.error('Error fetching properties on zoom:', error);
                onPropertiesUpdate?.([]);
                setShowNoPropsMsg(true);
              }
            })();
          }
        }
      }, 500);
    };
    map.on('moveend', handleMoveEnd);

    return () => {
      console.log('Cleaning up map...');
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (clusterLabelLayerRef.current) {
        try { clusterLabelLayerRef.current.clearLayers(); } catch (e) {}
      }
      if (clusterGroupRef.current) {
        try { clusterGroupRef.current.clearLayers(); } catch (e) { console.warn(e); }
      }
      if (mapRef.current) {
        try { mapRef.current.off(); mapRef.current.remove(); } catch (e) { console.warn(e); }
        mapRef.current = null;
      }
      setMapReady(false);
      setIsMapInitializing(true);
      setShowInitialSpinner(true);
    };
  }, [refreshClusterLabels]);

  // Refetch when filters change
  useEffect(() => {
    if (!mapReady || isMapInitializing || !mapRef.current) return;
    console.log('Filters changed, refetching with:', filters);
    const viewport = getCurrentViewport();
    if (!viewport) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchProperties(viewport);
  }, [filters, mapReady, isMapInitializing, getCurrentViewport, fetchProperties]);

  // Update markers
  useEffect(() => {
    if (!mapReady || !clusterGroupRef.current || isMapInitializing) return;

    try {
      const cluster = clusterGroupRef.current;
      if (!cluster) return;

      cluster.clearLayers();
      markerByIdRef.current.clear();

      if (clusterLabelLayerRef.current) clusterLabelLayerRef.current.clearLayers();
      clusterLabelMapRef.current.clear();

      if (!properties.length) return;

      const markers = [];
      const usePriceTags = currentZoom >= PRICE_TAG_ZOOM;

      properties.forEach(property => {
        const lat = parseFloat(property.lat);
        const lng = parseFloat(property.lng);

        if (isNaN(lat) || isNaN(lng)) {
          console.warn("Skipping invalid property:", property);
          return;
        }

        const isSelected = selectedId === property.id;
        let icon;

        if (usePriceTags && property.price) {
          const categoryColor = categoryColorMap[property.categoryName] || '#1a73e8';
          icon = buildSimplePriceTagIcon(
            property.price,
            property.categoryName,
            isSelected,
            categoryColor
          );
        } else {
          icon = getMarkerIcon(property.categoryName, isSelected, categoryColorMap);
        }

        const marker = L.marker([lat, lng], { icon, categoryName: property.categoryName });
        marker.on('click', () => onMarkerClick(property));
        markerByIdRef.current.set(property.id, marker);
        markers.push(marker);
      });

      cluster.addLayers(markers);

      setTimeout(() => refreshClusterLabels(), 400);

    } catch (error) {
      console.error('Error updating markers:', error);
    }
  }, [properties, selectedId, mapReady, isMapInitializing, onMarkerClick, currentZoom, categoryColorMap, refreshClusterLabels]);

  // Fly to target
  useEffect(() => {
    if (!mapReady || !flyTarget || !mapRef.current || isMapInitializing) return;

    const lat = parseFloat(flyTarget.lat);
    const lng = parseFloat(flyTarget.lng);
    const targetZoom = flyTarget.zoom;

    if (isNaN(lat) || isNaN(lng)) {
      console.error("Invalid flyTarget:", flyTarget);
      return;
    }

    isFlyingRef.current = true;

    const map = mapRef.current;
    const currentZoom = map.getZoom();

    if (Math.abs(currentZoom - targetZoom) < 0.5) {
      map.panTo([lat, lng], {
        duration: 0.8,
        easeLinearity: 0.25
      });
    } else {
      map.flyTo([lat, lng], targetZoom, {
        duration: 1.0,
        easeLinearity: 0.5
      });
    }

    const resetAndFetch = () => {
      isFlyingRef.current = false;
      map.off('moveend', resetAndFetch);

      const viewport = getCurrentViewport();
      if (viewport) {
        fetchProperties(viewport);
      }
    };

    map.once('moveend', resetAndFetch);

    setTimeout(() => {
      isFlyingRef.current = false;
    }, 2000);

  }, [flyTarget, mapReady, isMapInitializing, getCurrentViewport, fetchProperties]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 0,
          backgroundColor: 'transparent'
        }}
      />

      {showInitialSpinner && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderRadius: '12px', padding: '24px 32px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{
              width: '48px', height: '48px',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              borderTopColor: '#fff', borderRightColor: '#fff',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite'
            }} />
            <div style={{ color: '#fff', fontSize: '16px', fontWeight: '500', fontFamily: 'Arial, sans-serif' }}>
              Loading properties...
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <CopySuccessAlert message={alertMessage} onClose={() => { setShowSuccess(false); setAlertMessage(""); }} />
      )}
      {showFailure && (
        <CopyFailureAlert message={alertMessage} onClose={() => { setShowFailure(false); setAlertMessage(""); }} />
      )}

      {showNoPropsMsg && !showInitialSpinner && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1001,
          backgroundColor: 'rgba(0,0,0,0.85)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '280px',
          backdropFilter: 'blur(8px)',
          pointerEvents: 'none'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '8px' }}>
            {filters.priceMin > 0 || filters.priceMax < 1000000000 ? '💰' :
             filters.propertyType?.length > 0 ? '🏷️' : '🔍'}
          </div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {filters.priceMin > 0 || filters.priceMax < 1000000000 ? 'No Properties in Price Range' :
             filters.propertyType?.length > 0 ? 'No Properties in Category' :
             'No Properties in Area'}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.9 }}>
            {filters.priceMin > 0 || filters.priceMax < 1000000000 ?
             `No properties found between ₹${filters.priceMin?.toLocaleString()} - ₹${filters.priceMax?.toLocaleString()}. Try adjusting price range.` :
             filters.propertyType?.length > 0 ?
             `No properties found for selected categories. Try different categories.` :
             'No properties available in this zoom area. Try zooming out to see more results.'}
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .csl-wrap { background: transparent !important; border: none !important; box-shadow: none !important; }
      `}</style>
    </>
  );
};

export default RentMapView;